import React from 'react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { formatPrice } from '../lib/currency';
import { Star, MapPin, Clock, Check, ExternalLink, ChevronDown, Wifi, Coffee, ShieldCheck } from 'lucide-react';
import { SaveButton } from './SaveButton';

export const HotelCard = ({ offer, currency, onViewDeal, onOpenDetail }) => {
  const [showDetails, setShowDetails] = React.useState(false);
  const perNight = offer.nights ? Math.round(offer.price_inr / offer.nights) : offer.price_inr;
  const openPage = () => onOpenDetail && onOpenDetail(offer);
  return (
    <div className="rounded-2xl border border-border bg-card overflow-hidden card-elevate" data-testid="hotel-result-card">
      <div className="flex flex-col sm:flex-row">
        <div className={`sm:w-56 h-44 sm:h-auto shrink-0 relative ${onOpenDetail ? 'cursor-pointer' : ''}`} onClick={openPage}>
          <img src={offer.image} alt={offer.name} loading="lazy" className="w-full h-full object-cover" />
          {offer.discount_percentage > 0 && (
            <span className="absolute top-2 left-2 text-[10px] font-semibold bg-emerald-600 text-white px-1.5 py-0.5 rounded" data-testid="hotel-discount-badge">-{offer.discount_percentage}%</span>
          )}
        </div>
        <div className="flex-1 p-4 flex flex-col">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <div className="flex items-center gap-1 mb-0.5">
                {Array.from({ length: offer.stars || 0 }).map((_, i) => <Star key={i} className="h-3.5 w-3.5 fill-amber-400 text-amber-400" />)}
              </div>
              <h3 className={`font-semibold text-base truncate ${onOpenDetail ? 'hover:text-accent cursor-pointer' : ''}`} onClick={openPage} data-testid="hotel-card-name">{offer.name}</h3>
              <p className="text-xs text-muted-foreground flex items-center gap-1"><MapPin className="h-3 w-3" /> {offer.location}</p>
            </div>
            {offer.review_score ? (
              <div className="text-right shrink-0">
                <span className="inline-flex items-center gap-1 rounded-lg bg-accent/12 text-accent px-2 py-1 text-sm font-semibold tabular">{offer.review_score}</span>
                {offer.review_count ? <p className="text-[11px] text-muted-foreground mt-0.5">{offer.review_count.toLocaleString('en-IN')} reviews</p> : null}
              </div>
            ) : null}
          </div>
          <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
            <span>{offer.room_type} · {offer.nights} night{offer.nights > 1 ? 's' : ''}</span>
            {offer.include_breakfast ? <span className="inline-flex items-center gap-1 text-emerald-600"><Coffee className="h-3 w-3" /> Breakfast</span> : null}
            {offer.free_wifi ? <span className="inline-flex items-center gap-1 text-emerald-600"><Wifi className="h-3 w-3" /> Free Wi-Fi</span> : null}
          </div>
          <div className="mt-auto pt-3 flex items-end justify-between gap-2">
            <div>
              {offer.crossed_out_inr ? <p className="text-xs text-muted-foreground line-through tabular">{formatPrice(offer.crossed_out_inr, currency)}</p> : null}
              {offer.price_inr ? (
                <>
                  <p className="text-xl font-semibold tabular">{formatPrice(offer.price_inr, currency)}</p>
                  <p className="text-[11px] text-muted-foreground">total · {offer.nights} night{offer.nights > 1 ? 's' : ''}</p>
                </>
              ) : (
                <>
                  <p className="text-base font-semibold text-accent">Check latest price</p>
                  <p className="text-[11px] text-muted-foreground">rates with our partner</p>
                </>
              )}
            </div>
            <div className="flex flex-col items-end gap-1.5">
              <div className="flex items-center gap-2">
                <SaveButton itemType="hotel" title={offer.name} data={offer} />
                <Button variant="outline" onClick={() => setShowDetails((v) => !v)} className="h-9 rounded-xl font-semibold border-accent/40 text-accent hover:bg-accent/10" data-testid="hotel-view-details-button">
                  View details <ChevronDown className={`h-3.5 w-3.5 ml-1 transition-transform ${showDetails ? 'rotate-180' : ''}`} />
                </Button>
                <Button onClick={() => onViewDeal(offer)} className="h-9 rounded-xl bg-accent text-accent-foreground hover:bg-accent/90 font-semibold" data-testid="view-deal-button">
                  {offer.price_inr ? 'BOOK NOW' : 'CHECK PRICE'} <ExternalLink className="h-3.5 w-3.5 ml-1.5" />
                </Button>
              </div>
              <p className="text-[10px] text-muted-foreground" data-testid="view-deal-opens-partner-subtext">Opens partner website</p>
            </div>
          </div>
        </div>
      </div>

      {showDetails && (
        <div className="border-t border-border bg-secondary/20 px-4 py-3 space-y-2.5" data-testid="hotel-details-section">
          {(offer.images && offer.images.length > 1) ? (
            <div className="flex gap-2 overflow-x-auto pb-1" data-testid="hotel-details-gallery">
              {offer.images.slice(0, 5).map((src, i) => (
                <img key={i} src={src} alt={`${offer.name} ${i + 1}`} loading="lazy" className="h-24 w-32 shrink-0 object-cover rounded-lg" />
              ))}
            </div>
          ) : null}
          <p className="text-sm font-semibold">{offer.name} <span className="font-normal text-muted-foreground">· {offer.accommodation_type || 'Hotel'}</span></p>
          {offer.address ? <p className="text-[11px] text-muted-foreground inline-flex items-center gap-1"><MapPin className="h-3 w-3" /> {offer.address}{offer.state ? `, ${offer.state}` : ''}</p> : null}
          <div className="flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-muted-foreground" data-testid="hotel-details-amenities">
            {offer.stars ? <span className="inline-flex items-center gap-1"><Star className="h-3 w-3 fill-amber-400 text-amber-400" /> {offer.stars}-star</span> : null}
            {offer.review_score ? <span>Guest rating {offer.review_score}/10 · {(offer.review_count || 0).toLocaleString('en-IN')} reviews</span> : null}
            <span className="inline-flex items-center gap-1"><Coffee className="h-3 w-3" /> Breakfast: {offer.include_breakfast ? 'included' : 'not included'}</span>
            <span className="inline-flex items-center gap-1"><Wifi className="h-3 w-3" /> Wi-Fi: {offer.free_wifi ? 'free' : 'not included'}</span>
          </div>
          {offer.overview ? <p className="text-xs text-muted-foreground leading-relaxed line-clamp-4">{offer.overview}</p> : null}
          {offer.price_inr ? (
            <div className="text-xs text-foreground" data-testid="hotel-details-price">
              {formatPrice(perNight, currency)} <span className="text-muted-foreground">/ night</span> × {offer.nights} night{offer.nights > 1 ? 's' : ''} = <span className="font-semibold">{formatPrice(offer.price_inr, currency)}</span>
              {offer.discount_percentage > 0 ? <span className="ml-2 text-emerald-600 font-medium">You save {offer.discount_percentage}%</span> : null}
            </div>
          ) : (
            <p className="text-xs text-accent font-medium" data-testid="hotel-details-price">Live rates for your dates are shown with our booking partner.</p>
          )}
          <p className="text-[11px] text-muted-foreground inline-flex items-center gap-1"><ShieldCheck className="h-3 w-3" /> You'll review the room, taxes &amp; cancellation policy and complete your booking securely with our trusted partner.</p>
          {onOpenDetail && <button onClick={openPage} className="text-xs text-accent font-semibold hover:underline" data-testid="hotel-open-full-page">View full page &amp; all photos →</button>}
          <Button onClick={() => onViewDeal(offer)} className="w-full h-10 rounded-xl bg-accent text-accent-foreground hover:bg-accent/90 font-semibold mt-1" data-testid="hotel-details-book-button">
            {offer.price_inr ? 'BOOK NOW' : 'CHECK PRICE ON AGODA'} <ExternalLink className="h-4 w-4 ml-1.5" />
          </Button>
        </div>
      )}
    </div>
  );
};

export const ExperienceCard = ({ offer, currency, onViewDeal }) => (
  <div role="button" tabIndex={0} onClick={() => onViewDeal(offer)}
    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onViewDeal(offer); } }}
    className="rounded-2xl border border-border bg-card overflow-hidden card-elevate flex flex-col cursor-pointer group focus-visible:ring-2 focus-visible:ring-accent focus-visible:outline-none" data-testid="experience-result-card">
    <div className="h-40 relative">
      <img src={offer.image} alt={offer.title} loading="lazy" className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105" />
      <Badge className="absolute top-2 left-2 bg-card/90 text-foreground border-border rounded-full text-[10px]">{offer.category}</Badge>
    </div>
    <div className="p-4 flex flex-col flex-1">
      <h3 className="font-semibold text-sm leading-snug line-clamp-2">{offer.title}</h3>
      <div className="mt-1 flex items-center gap-2 text-xs text-muted-foreground">
        {offer.rating ? <span className="inline-flex items-center gap-1"><Star className="h-3 w-3 fill-amber-400 text-amber-400" /> {offer.rating}{offer.review_count ? ` (${offer.review_count.toLocaleString('en-IN')})` : ''}</span> : null}
        {offer.duration_hours ? <span className="inline-flex items-center gap-1"><Clock className="h-3 w-3" /> {offer.duration_hours}h</span> : null}
        {offer.free_cancellation ? <span className="inline-flex items-center gap-1 text-emerald-600"><Check className="h-3 w-3" /> Free cancellation</span> : null}
      </div>
      <div className="mt-auto pt-3 flex items-end justify-between">
        <div>
          {offer.price_inr ? (
            <>
              <p className="text-lg font-semibold tabular">{formatPrice(offer.price_inr, currency)}</p>
              <p className="text-[11px] text-muted-foreground">per person</p>
            </>
          ) : (
            <p className="text-sm text-muted-foreground">See price</p>
          )}
        </div>
        <div className="text-right flex items-center gap-2">
          <SaveButton itemType="experience" title={offer.title} data={offer} className="h-9 w-9" />
          <div>
          <Button onClick={(e) => { e.stopPropagation(); onViewDeal(offer); }} size="sm" className="h-9 rounded-xl bg-accent text-accent-foreground hover:bg-accent/90 font-semibold" data-testid="view-experience-button">
            View details <ExternalLink className="h-3.5 w-3.5 ml-1" />
          </Button>
          <p className="text-[10px] text-muted-foreground mt-1" data-testid="view-deal-opens-partner-subtext">See details & book</p>
          </div>
        </div>
      </div>
    </div>
  </div>
);

export const DealCard = ({ deal, currency, onViewDeal }) => (
  <div className="rounded-2xl border border-border bg-card overflow-hidden card-elevate group" data-testid="deal-card">
    <div className="h-40 relative overflow-hidden">
      <img src={deal.image} alt={deal.title} loading="lazy" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
      <div className="absolute inset-0 bg-gradient-to-t from-primary/70 to-transparent" />
      <Badge className="absolute top-2 right-2 bg-accent text-accent-foreground rounded-full text-[10px] font-semibold">{deal.badge}</Badge>
      <div className="absolute bottom-3 left-3 text-primary-foreground">
        <p className="font-display font-semibold text-lg leading-tight">{deal.title}</p>
        <p className="text-xs opacity-80">{deal.scope} · {deal.type}</p>
      </div>
    </div>
    <div className="p-4 flex items-end justify-between">
      <div>
        <p className="text-[11px] text-muted-foreground">from</p>
        <p className="text-xl font-semibold tabular">{formatPrice(deal.price, currency)}</p>
      </div>
      <div className="text-right">
        <Button onClick={() => onViewDeal(deal)} className="h-9 rounded-xl bg-accent text-accent-foreground hover:bg-accent/90 font-semibold" data-testid="view-deal-button">
          VIEW DEAL <ExternalLink className="h-3.5 w-3.5 ml-1.5" />
        </Button>
        <p className="text-[10px] text-muted-foreground mt-1" data-testid="view-deal-opens-partner-subtext">Opens partner site</p>
      </div>
    </div>
  </div>
);
